// assets/hbk-testimonials.js
(function () {
  'use strict';

  const SELECTORS = {
    root:        '[data-component="testimonials"]',
    track:       '[data-ts-track]',
    prev:        '[data-ts-prev]',
    next:        '[data-ts-next]',
    dots:        '[data-ts-dots]',
    card:        '.hbk-ts__card',
    summaryFill: '.hbk-ts__summary-bar-fill',
  };

  const instances = new Map();

  class Testimonials {
    constructor(el) {
      this.el            = el;
      this.track         = el.querySelector(SELECTORS.track);
      this.prevBtn       = el.querySelector(SELECTORS.prev);
      this.nextBtn       = el.querySelector(SELECTORS.next);
      this.dotsContainer = el.querySelector(SELECTORS.dots);
      this.cards         = Array.from(this.track?.querySelectorAll(SELECTORS.card) || []);
      this.colsDesktop   = parseInt(el.dataset.colsDesktop) || 3;
      this.colsMobile    = parseInt(el.dataset.colsMobile) || 1;
      this.focusMode     = el.dataset.focusEffect === 'true';
      this.current       = 0;
      this.autoplayTimer = null;
      this._touchStartX  = 0;
      this._init();
    }

    get _cols() {
      if (this.focusMode) return 1;
      return window.innerWidth >= 990 ? this.colsDesktop : this.colsMobile;
    }

    get _pageCount() {
      return this.focusMode
        ? this.cards.length
        : Math.ceil(this.cards.length / this._cols);
    }

    _init() {
      if (!this.track || this.cards.length === 0) return;

      if (this.focusMode) this._initFocusMode();

      this._buildDots();
      this._updateNav();

      this.prevBtn?.addEventListener('click', () => this._move(-1));
      this.nextBtn?.addEventListener('click', () => this._move(1));

      this._bindTouch();
      this._bindKeyboard();
      this._startAutoplay();

      this._resizeObserver = new ResizeObserver(() => {
        if (this.focusMode) this._initFocusMode();
        this._buildDots();
        this._goTo(this.current);
      });
      this._resizeObserver.observe(this.el);
    }

    _initFocusMode() {
      // Add padding so the first and last cards can be visually centered
      const wrapperWidth = this.el.offsetWidth;
      const cardWidth    = this.cards[0]?.offsetWidth || 300;
      const pad          = Math.max(0, (wrapperWidth - cardWidth) / 2);
      this.track.style.paddingInline = `${pad}px`;
      this._updateFocusClasses();
    }

    _buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      const count = this._pageCount;
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.className = 'hbk-ts__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Page ${i + 1} of ${count}`);
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        dot.addEventListener('click', () => this._goTo(i));
        this.dotsContainer.appendChild(dot);
      }
    }

    _move(dir) {
      const next = this.current + dir;
      if (next < 0 || next >= this._pageCount) return;
      this._goTo(next);
    }

    _goTo(page) {
      this.current = Math.max(0, Math.min(page, this._pageCount - 1));
      const cardEl = this.cards[this.focusMode ? this.current : 0];
      if (!cardEl) return;

      const gap = parseInt(getComputedStyle(this.track).gap) || 0;

      if (this.focusMode) {
        // In focus mode, cards move one at a time and the active card is centered
        const cardWidth = this.cards[0].offsetWidth;
        const offset    = this.current * (cardWidth + gap);
        this.track.style.transform = `translateX(-${offset}px)`;
        this._updateFocusClasses();
      } else {
        const slideUnit = (cardEl.offsetWidth + gap) * this._cols;
        this.track.style.transform = `translateX(-${this.current * slideUnit}px)`;
      }

      this._updateNav();
      this._updateDots();
    }

    _updateFocusClasses() {
      this.cards.forEach((card, i) => {
        card.classList.remove('is-center', 'is-adjacent');
        if (i === this.current) {
          card.classList.add('is-center');
        } else if (Math.abs(i - this.current) === 1) {
          card.classList.add('is-adjacent');
        }
      });
    }

    _updateNav() {
      const atStart = this.current === 0;
      const atEnd   = this.current >= this._pageCount - 1;
      if (this.prevBtn) {
        this.prevBtn.disabled = atStart;
        this.prevBtn.setAttribute('aria-disabled', String(atStart));
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = atEnd;
        this.nextBtn.setAttribute('aria-disabled', String(atEnd));
      }
    }

    _updateDots() {
      this.dotsContainer?.querySelectorAll('.hbk-ts__dot').forEach((dot, i) => {
        const active = i === this.current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    _bindTouch() {
      this.track?.addEventListener('touchstart', e => {
        this._touchStartX = e.touches[0].clientX;
      }, { passive: true });

      this.track?.addEventListener('touchend', e => {
        const diff = this._touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) this._move(diff > 0 ? 1 : -1);
      }, { passive: true });
    }

    _bindKeyboard() {
      this.el.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  this._move(-1);
        if (e.key === 'ArrowRight') this._move(1);
      });
    }

    _startAutoplay() {
      const ms = parseInt(this.el.dataset.autoplaySpeed) || 0;
      if (!ms || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.autoplayTimer = setInterval(() => {
        if (this.current >= this._pageCount - 1) {
          this._goTo(0);
        } else {
          this._move(1);
        }
      }, ms);
    }

    scrollToCard(cardEl) {
      const idx = this.cards.indexOf(cardEl);
      if (idx < 0) return;
      this._goTo(this.focusMode ? idx : Math.floor(idx / this._cols));
    }

    destroy() {
      clearInterval(this.autoplayTimer);
      this._resizeObserver?.disconnect();
    }
  }

  function initSummaryBars(scope) {
    scope.querySelectorAll(SELECTORS.summaryFill).forEach(fill => {
      const targetWidth = fill.dataset.fillWidth;
      if (!targetWidth) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        fill.style.width = targetWidth + '%';
        return;
      }
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          fill.style.width = targetWidth + '%';
          observer.disconnect();
        });
      }, { threshold: 0.5 });
      observer.observe(fill);
    });
  }

  function init(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new Testimonials(el));
    });
    initSummaryBars(scope instanceof Document ? scope.body : scope);
  }

  function destroy(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (!instances.has(el)) return;
      instances.get(el).destroy();
      instances.delete(el);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load',   e => init(e.target));
  document.addEventListener('shopify:section:unload', e => destroy(e.target));

  document.addEventListener('shopify:block:select', e => {
    const carousel = e.target.closest(SELECTORS.root);
    if (!carousel || !instances.has(carousel)) return;
    instances.get(carousel).scrollToCard(e.target);
  });
})();
